import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import {
  createCartItem,
  deleteCartItem,
  loadCartItemsForUser,
  updateCartItem,
} from '../api/cartApi'
import {
  clearShopUserValidationCache,
  getAssetIdFromProduct,
  resolveShopUserId,
} from '../api/shopAuth'

const CartContext = createContext(null)
const MAX_QUANTITY = 20

function getProductId(product) {
  const id = getAssetIdFromProduct(product)
  return id == null ? '' : String(id)
}

export function CartProvider({ children }) {
  const navigate = useNavigate()
  const [userId, setUserId] = useState(null)
  const [authReady, setAuthReady] = useState(false)
  const [items, setItems] = useState([])
  const [isSyncing, setIsSyncing] = useState(false)
  const itemsRef = useRef(items)
  itemsRef.current = items

  const refreshAuth = useCallback(async () => {
    clearShopUserValidationCache()
    setAuthReady(false)
    try {
      const id = await resolveShopUserId()
      setUserId(id)
    } finally {
      setAuthReady(true)
    }
  }, [])

  useEffect(() => {
    refreshAuth()
    const onAuth = () => {
      refreshAuth()
    }
    window.addEventListener('auth-changed', onAuth)
    window.addEventListener('storage', onAuth)
    return () => {
      window.removeEventListener('auth-changed', onAuth)
      window.removeEventListener('storage', onAuth)
    }
  }, [refreshAuth])

  useEffect(() => {
    if (!authReady) return undefined

    if (userId == null) {
      setItems([])
      return undefined
    }

    let cancelled = false
    setIsSyncing(true)
    ;(async () => {
      try {
        const loaded = await loadCartItemsForUser(userId)
        if (!cancelled) {
          setItems(
            loaded.map((item) => ({
              cartId: item.cartId,
              product: item.product,
              quantity: item.quantity,
            }))
          )
        }
      } catch {
        if (!cancelled) setItems([])
      } finally {
        if (!cancelled) setIsSyncing(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [authReady, userId])

  const requireLogin = useCallback(() => {
    if (userId == null) {
      navigate('/login', {
        state: {
          from: window.location.pathname + window.location.search,
          message: 'Please login to manage your cart',
        },
      })
      return false
    }
    return true
  }, [navigate, userId])

  const addToCart = useCallback(
    async (product) => {
      if (!authReady) return { ok: false, reason: 'loading' }
      if (!requireLogin()) return { ok: false, reason: 'login' }

      const productId = getProductId(product)
      const assetId = getAssetIdFromProduct(product)
      if (!productId || assetId == null) {
        return { ok: false, reason: 'invalid', message: 'Invalid product' }
      }

      const existing = itemsRef.current.find(
        (item) => getProductId(item.product) === productId
      )
      const nextQuantity = existing
        ? Math.min(existing.quantity + 1, MAX_QUANTITY)
        : 1

      setItems((prev) => {
        const index = prev.findIndex(
          (item) => getProductId(item.product) === productId
        )
        if (index === -1) {
          return [...prev, { product, quantity: 1 }]
        }
        return prev.map((item, i) =>
          i === index ? { ...item, quantity: nextQuantity } : item
        )
      })

      try {
        if (existing?.cartId) {
          await updateCartItem(existing.cartId, {
            quantity: nextQuantity,
            user_id: userId,
            asset_id: assetId,
          })
          return {
            ok: true,
            updated: true,
            alreadyInCart: true,
            quantity: nextQuantity,
          }
        }

        const created = await createCartItem({
          user_id: userId,
          asset_id: assetId,
          quantity: nextQuantity,
        })
        setItems((prev) =>
          prev.map((item) =>
            getProductId(item.product) === productId
              ? {
                  ...item,
                  cartId: Number(created.cart_id),
                  quantity: nextQuantity,
                }
              : item
          )
        )
        return { ok: true, added: true, quantity: nextQuantity }
      } catch (error) {
        try {
          const refreshed = await loadCartItemsForUser(userId)
          setItems(
            refreshed.map((item) => ({
              cartId: item.cartId,
              product: item.product,
              quantity: item.quantity,
            }))
          )
          if (
            refreshed.some((item) => getProductId(item.product) === productId)
          ) {
            return { ok: true, added: true }
          }
        } catch {
          setItems((prev) => prev.filter((item) => item.cartId != null))
        }
        return {
          ok: false,
          reason: 'error',
          message: error.message || 'Failed to update cart',
        }
      }
    },
    [authReady, requireLogin, userId]
  )

  const setQuantity = useCallback(
    async (productId, quantity) => {
      if (!requireLogin()) return { ok: false, reason: 'login' }
      const id = String(productId)
      const assetId = Number(productId)
      const existing = itemsRef.current.find(
        (item) => getProductId(item.product) === id
      )
      if (!existing) return { ok: false }

      const nextQuantity = Math.max(
        0,
        Math.min(Number(quantity) || 0, MAX_QUANTITY)
      )

      if (nextQuantity <= 0) {
        setItems((prev) =>
          prev.filter((item) => getProductId(item.product) !== id)
        )
        try {
          if (existing.cartId) await deleteCartItem(existing.cartId)
          return { ok: true, removed: true }
        } catch (error) {
          setItems((prev) => [...prev, existing])
          return {
            ok: false,
            message: error.message || 'Failed to update cart',
          }
        }
      }

      setItems((prev) =>
        prev.map((item) =>
          getProductId(item.product) === id
            ? { ...item, quantity: nextQuantity }
            : item
        )
      )

      try {
        if (existing.cartId) {
          await updateCartItem(existing.cartId, {
            quantity: nextQuantity,
            user_id: userId,
            asset_id: assetId,
          })
        }
        return { ok: true }
      } catch (error) {
        setItems((prev) =>
          prev.map((item) =>
            getProductId(item.product) === id
              ? { ...item, quantity: existing.quantity }
              : item
          )
        )
        return {
          ok: false,
          message: error.message || 'Failed to update cart',
        }
      }
    },
    [requireLogin, userId]
  )

  const removeFromCart = useCallback(
    async (productId) => setQuantity(productId, 0),
    [setQuantity]
  )

  const clearCart = useCallback(async () => {
    const snapshot = itemsRef.current
    setItems([])
    if (!userId) return
    await Promise.all(
      snapshot
        .filter((item) => item.cartId != null)
        .map((item) => deleteCartItem(item.cartId).catch(() => null))
    )
  }, [userId])

  const isInCart = useCallback(
    (productId) =>
      items.some((item) => getProductId(item.product) === String(productId)),
    [items]
  )

  const cartCount = useMemo(
    () => items.reduce((total, item) => total + item.quantity, 0),
    [items]
  )

  const value = useMemo(
    () => ({
      items,
      cartCount,
      isSyncing: isSyncing || !authReady,
      isAuthenticated: userId != null,
      isInCart,
      addToCart,
      setQuantity,
      removeFromCart,
      clearCart,
    }),
    [
      items,
      cartCount,
      isSyncing,
      authReady,
      userId,
      isInCart,
      addToCart,
      setQuantity,
      removeFromCart,
      clearCart,
    ]
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
